"use client";

/**
 * Full-card lock overlay for gated features (meal suggestions, weekend plans,
 * receipt scanning, etc.) when the household isn't on a paid/trial plan.
 *
 * Renders a dimmed blocking overlay with a Zoey+ lock icon and a "Subscribe"
 * CTA that opens the paywall. Used by wrapping the underlying feature UI:
 *
 *     <LockedFeatureOverlay locked={!state.isActive} feature="weekend_plans">
 *       <WeekendContent />
 *     </LockedFeatureOverlay>
 */

import type { ReactNode } from "react";
import { usePaywall } from "@/contexts/PaywallContext";
import { useTranslations } from "@/contexts/I18nContext";

interface LockedFeatureOverlayProps {
  locked: boolean;
  children: ReactNode;
  /** Shown on the overlay card. Defaults to a generic copy. */
  title?: string;
  /** Secondary copy under the title. */
  description?: string;
  /** Override paywall modal copy when user taps. */
  paywallTitle?: string;
  paywallSubtitle?: string;
}

export function LockedFeatureOverlay({
  locked,
  children,
  title,
  description,
  paywallTitle,
  paywallSubtitle,
}: LockedFeatureOverlayProps) {
  const { openPaywall } = usePaywall();
  const t = useTranslations();
  const resolvedTitle = title ?? t("locked.defaultTitle");
  const resolvedDescription = description ?? t("locked.defaultDescription");

  if (!locked) return <>{children}</>;

  return (
    <div className="relative">
      {/* Dim the underlying content and prevent interaction. */}
      <div
        aria-hidden="true"
        className="pointer-events-none select-none opacity-40 blur-[1px]"
      >
        {children}
      </div>

      <button
        onClick={() =>
          openPaywall({
            title: paywallTitle,
            subtitle: paywallSubtitle,
          })
        }
        className="absolute inset-0 z-10 w-full h-full flex flex-col items-center justify-center gap-2 rounded-2xl bg-white/80 dark:bg-gray-900/80 backdrop-blur-sm border border-dashed border-[color:var(--accent-color,#00C7BE)] transition-colors hover:bg-white/90 dark:hover:bg-gray-900/90"
        aria-label={t("locked.aria")}
      >
        <ZoeyPlusLockBadge />
        <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
          {resolvedTitle}
        </p>
        <p className="text-xs text-gray-600 dark:text-gray-400 text-center max-w-xs px-4">
          {resolvedDescription}
        </p>
        <span
          className="mt-2 inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold text-white"
          style={{ backgroundColor: "var(--accent-color, #00C7BE)" }}
        >
          {t("paywall.subscribe")}
          <svg
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M9 18l6-6-6-6" />
          </svg>
        </span>
      </button>
    </div>
  );
}

export function ZoeyPlusLockBadge() {
  return (
    <div
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wide text-white"
      style={{ backgroundColor: "var(--accent-color, #00C7BE)" }}
    >
      <svg
        width="12"
        height="12"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <rect x="3" y="11" width="18" height="10" rx="2" />
        <path d="M7 11V7a5 5 0 0110 0v4" />
      </svg>
      <span>Zoey+</span>
    </div>
  );
}
